import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";

function payloadFromData(data) {
  const y = parseInt(data.vehicle.year, 10);
  return {
    vehicle: {
      registration: data.vehicle.registration || "",
      make: data.vehicle.make,
      model: data.vehicle.model,
      year: Number.isNaN(y) ? null : y,
      fuel: data.vehicle.fuel || "",
      body_type: data.vehicle.body_type || "",
      vehicle_size: data.vehicle.vehicle_size,
    },
    condition: data.condition,
    lowered: data.lowered,
    journey: {
      pickup_location: data.journey.pickup_location,
      dropoff_location: data.journey.dropoff_location,
      miles: Number(data.journey.miles || 0),
    },
    collection: {
      date: data.collection.date,
      time_slot: data.collection.time_slot,
      flexibility: data.collection.flexibility || "none",
    },
    access: data.access,
    auction: data.auction,
  };
}

export default function StepQuote({ data, onNext, onBack, setPreview }) {
  const [loading, setLoading] = useState(true);
  const [preview, setLocal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: r } = await publicApi.post(
          "/quote/preview",
          payloadFromData(data)
        );
        if (cancelled) return;
        setLocal(r);
        setPreview(r);
      } catch (e) {
        console.error(e);
        toast.error("Could not calculate your quote. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#E60000]" />
        <div className="mt-4 text-zinc-400 text-sm tracking-widest uppercase">
          Building your quote…
        </div>
      </div>
    );
  }

  if (!preview) return null;

  if (preview.manual_quote_required) {
    return (
      <motion.div
        key="quote-manual"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
        data-testid="quote-manual-required"
      >
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
            Custom Quote Required
          </div>
          <h1 className="font-display font-extrabold text-white text-3xl md:text-5xl leading-[1.05] tracking-tight">
            Your transport needs a
            <br />
            <span className="text-stroke">closer look.</span>
          </h1>
        </div>
        <p className="text-zinc-400 text-base leading-relaxed max-w-xl">
          Based on what you’ve told us, we need a little more information
          before we can price your job accurately. Continue to submit your
          details and we’ll come back to you shortly with a tailored quote.
        </p>
        <ul className="space-y-2">
          {preview.manual_reasons.map((r, i) => (
            <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
              <span className="text-[#E60000]">·</span>
              {r}
            </li>
          ))}
        </ul>
        <div className="flex justify-between pt-2 gap-3">
          <button type="button" onClick={onBack} className="btn-ghost">
            Back
          </button>
          <button
            type="button"
            onClick={onNext}
            className="btn-primary"
            data-testid="quote-manual-continue"
          >
            Continue with custom quote
          </button>
        </div>
      </motion.div>
    );
  }

  const money = (n) => `£${Number(n).toLocaleString("en-GB")}`;

  return (
    <motion.div
      key="quote-result"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="space-y-8"
      data-testid="quote-result"
    >
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
          Step 04 · Your Quote
        </div>
        <h1 className="font-display font-extrabold text-white text-3xl md:text-5xl leading-[1.05] tracking-tight">
          Your Guaranteed
          <br />
          <span className="text-stroke">Transport Quote.</span>
        </h1>
        <p className="text-zinc-400 text-sm mt-4 max-w-xl">
          Based on the information you’ve provided, this is the price for the
          transport requested. Subject to the vehicle, access and locations
          being accurately described.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Standard Guaranteed Quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="border border-white/10 bg-[#0A0A0A] p-8"
          data-testid="quote-standard"
        >
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
            Standard Guaranteed Quote
          </div>
          <div className="font-display font-extrabold text-white text-5xl md:text-6xl mt-4">
            {money(preview.standard_guaranteed_quote)}
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            Valid for {preview.quote_validity_hours}h. Book at any time via
            phone, email or WhatsApp.
          </p>
        </motion.div>

        {/* Instant Booking Price */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="border border-[#E60000] bg-gradient-to-br from-[#E60000]/10 to-transparent p-8 relative overflow-hidden"
          data-testid="quote-instant"
        >
          <div className="absolute top-4 right-4 flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase text-[#E60000]">
            <CheckCircle2 className="w-3.5 h-3.5" /> Save now
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
            Instant Booking Price
          </div>
          <div className="font-display font-extrabold text-white text-5xl md:text-6xl mt-4">
            {money(preview.instant_booking_price)}
          </div>
          <p className="text-xs text-zinc-300 mt-4">
            Secure your transport online today for our best price.
          </p>
        </motion.div>
      </div>

      {preview.flags.length > 0 && (
        <div className="border border-white/10 bg-[#0A0A0A] p-4 text-xs text-zinc-400 space-y-1">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
            Requires our review
          </div>
          {preview.flags.map((f) => (
            <div key={f}>· {f.replace(/_/g, " ")}</div>
          ))}
        </div>
      )}

      <div className="flex justify-between pt-2 gap-3">
        <button type="button" onClick={onBack} className="btn-ghost">
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="btn-primary"
          data-testid="quote-book-now"
        >
          Secure my transport
        </button>
      </div>
    </motion.div>
  );
}
